import { useState, useEffect, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import SummaryMarkdown from "./SummaryMarkdown";

// Lobi'de "Geçmiş Özetler" — toplantıya GİRMEDEN, oda + tarih/saate göre listele, tıkla→biçimli özet.
// summaryApi = { listSummaries, getSummaryById, deleteSummary? }. Silme yalnız sahip (backend kontrol eder).
type Item = { id: number; room: string; created: number; preview?: string };
type Props = {
  summaryApi?: any;
  t?: (k: string, fb?: string) => string;
  onLoadingChange?: (loading: boolean) => void;
};

const unwrap = (r: any) => (r && r.data && r.data.result !== undefined ? r.data.result : (r && r.data) || r || null);

function fmtDate(ts: number, locale?: string) {
  try {
    return new Date(ts * 1000).toLocaleString(locale || undefined, {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return String(ts); }
}

export default function SummariesHistory({ summaryApi, t, onLoadingChange }: Props) {
  const tr = (k: string, fb: string) => (t ? t(k, fb) : fb);
  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(false);
  const [openId, setOpenId] = useState<number | null>(null);
  const [detail, setDetail] = useState<{ summary: string; room: string; created: number } | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [confirmId, setConfirmId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [copied, setCopied] = useState(false);

  const load = useCallback(() => {
    if (!summaryApi?.listSummaries) { onLoadingChange?.(false); return; }
    setLoading(true);
    onLoadingChange?.(true);
    summaryApi.listSummaries().then((r: any) => {
      const d = unwrap(r);
      setItems(Array.isArray(d?.items) ? d.items : []);
    }).catch(() => setItems([])).finally(() => {
      setLoading(false);
      onLoadingChange?.(false);
    });
  }, [summaryApi, onLoadingChange]);

  useEffect(() => { load(); }, [load]);

  const openDetail = useCallback((id: number) => {
    if (!summaryApi?.getSummaryById) return;
    setOpenId(id); setDetail(null); setDetailLoading(true);
    summaryApi.getSummaryById(id).then((r: any) => {
      const d = unwrap(r);
      if (d && d.summary) setDetail({ summary: String(d.summary), room: d.room, created: d.created });
    }).catch(() => {}).finally(() => setDetailLoading(false));
  }, [summaryApi]);

  // (E) Sahip silme — backend user_id eşleşmesini doğrular; başarısızsa liste değişmez.
  const removeItem = useCallback((id: number) => {
    if (!summaryApi?.deleteSummary) return;
    setDeletingId(id);
    summaryApi.deleteSummary(id).then((r: any) => {
      const d = unwrap(r);
      if (d && (d.deleted || d.success)) {
        setItems((prev) => prev.filter((x) => x.id !== id));
        setOpenId((cur) => (cur === id ? null : cur));
        setDetail((cur) => (cur && cur.room && openId === id ? null : cur));
      }
    }).catch(() => {}).finally(() => { setDeletingId(null); setConfirmId(null); });
  }, [summaryApi, openId]);

  const grouped = useMemo(() => {
    const map = new Map<string, Item[]>();
    for (const it of items) {
      if (!map.has(it.room)) map.set(it.room, []);
      map.get(it.room)!.push(it);
    }
    return Array.from(map.entries());
  }, [items]);

  const canDelete = !!summaryApi?.deleteSummary;

  if (!summaryApi?.listSummaries) return null;

  const copyDetail = () => {
    if (!detail?.summary) return;
    try { navigator.clipboard?.writeText(detail.summary); setCopied(true); setTimeout(() => setCopied(false), 1600); } catch (_) {}
  };

  const detailModal = openId != null ? createPortal(
    <div className="sh-backdrop" onClick={() => setOpenId(null)} role="dialog" aria-modal="true">
      <div className="sh-modal" onClick={(e) => e.stopPropagation()}>
        <div className="sh-mhead">
          <div className="sh-mhead-l">
            <span className="sh-mhead-ico" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 6h16M4 11h16M4 16h10"/><path d="M15.5 19l2 2 3.5-3.5"/></svg>
            </span>
            <div>
              <div className="sh-mtitle">{detail?.room || tr("memeetSummaryTitle", "Toplantı Özeti")}</div>
              {detail?.created ? <div className="sh-mdate">{fmtDate(detail.created)}</div> : null}
            </div>
          </div>
          <button className="sh-close" onClick={() => setOpenId(null)} aria-label="×">×</button>
        </div>
        <div className="sh-mbody">
          {detailLoading && <div className="sh-loading">{tr("memeetLoading", "Yükleniyor…")}</div>}
          {detail?.summary && <SummaryMarkdown source={detail.summary} />}
        </div>
        {detail?.summary && (
          <div className="sh-mfoot">
            <button className="sh-copy" onClick={copyDetail}>
              {copied ? tr("memeetSummaryCopied", "Kopyalandı") : tr("memeetSummaryCopy", "Kopyala")}
            </button>
            {canDelete && openId != null && (
              <button className="sh-del-text" onClick={() => removeItem(openId)} disabled={deletingId === openId}>
                {deletingId === openId ? tr("memeetDeleting", "Siliniyor…") : tr("memeetDelete", "Sil")}
              </button>
            )}
          </div>
        )}
      </div>
      <style>{SH_MODAL_CSS}</style>
    </div>,
    document.body
  ) : null;

  return (
    <section className="sh-root">
      <div className="sh-head">
        <h2>
          <span className="sh-head-ico" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M4 6h16M4 11h16M4 16h10"/><path d="M15.5 19l2 2 3.5-3.5"/></svg>
          </span>
          {tr("memeetHistoryTitle", "Geçmiş Özetler")}
          {items.length ? <span className="sh-count">{items.length}</span> : null}
        </h2>
        <button className="sh-refresh" onClick={load} title={tr("memeetRefresh", "Yenile")} aria-label={tr("memeetRefresh", "Yenile")}>
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M23 4v6h-6M1 20v-6h6"/><path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"/></svg>
        </button>
      </div>
      {loading && !items.length ? (
        <div className="sh-empty">{tr("memeetLoading", "Yükleniyor…")}</div>
      ) : !items.length ? (
        <div className="sh-empty">
          <span className="sh-empty-ico" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M4 6h16M4 11h16M4 16h10"/><path d="M15.5 19l2 2 3.5-3.5"/></svg>
          </span>
          {tr("memeetHistoryEmpty", "Henüz özet yok. Bir toplantıyı kaydedip özetlediğinizde burada listelenir.")}
        </div>
      ) : (
        <div className="sh-rooms">
          {grouped.map(([room, list]) => (
            <div className="sh-room" key={room}>
              <div className="sh-room-head">
                <span className="sh-room-ico" aria-hidden="true">
                  <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M15 10l5-3v10l-5-3v-4z"/><rect x="3" y="6" width="12" height="12" rx="2"/></svg>
                </span>
                <span className="sh-room-name">{room}</span>
                <span className="sh-room-count">{list.length}</span>
              </div>
              <ul className="sh-list">
                {list.map((it) => (
                  <li className="sh-li" key={it.id}>
                    <button className="sh-item" onClick={() => openDetail(it.id)}>
                      <span className="sh-item-main">
                        <span className="sh-item-date">{fmtDate(it.created)}</span>
                        {it.preview ? <span className="sh-item-preview">{it.preview}</span> : null}
                      </span>
                      <span className="sh-item-chev" aria-hidden="true">
                        <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 6l6 6-6 6"/></svg>
                      </span>
                    </button>
                    {canDelete ? (
                      confirmId === it.id ? (
                        <span className="sh-confirm">
                          <button className="sh-c-yes" disabled={deletingId === it.id} onClick={() => removeItem(it.id)}>
                            {deletingId === it.id ? tr("memeetDeleting", "Siliniyor…") : tr("memeetDelete", "Sil")}
                          </button>
                          <button className="sh-c-no" onClick={() => setConfirmId(null)}>{tr("memeetCancel", "Vazgeç")}</button>
                        </span>
                      ) : (
                        <button className="sh-del" title={tr("memeetDelete", "Sil")} aria-label={tr("memeetDelete", "Sil")} onClick={() => setConfirmId(it.id)}>
                          <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6M10 11v6M14 11v6"/></svg>
                        </button>
                      )
                    ) : null}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
      {detailModal}
      <style>{SH_CSS}</style>
    </section>
  );
}

const SH_CSS = `
.sh-root { margin-top: 28px; }
.sh-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 14px; }
.sh-head h2 { display: inline-flex; align-items: center; gap: 9px; font-size: 16px; font-weight: 700; margin: 0; color: inherit; }
.sh-head-ico { display: inline-flex; opacity: .85; }
.sh-count { font-size: 11.5px; font-weight: 700; padding: 1px 8px; border-radius: 999px; background: rgba(29,185,84,0.16); color: #57e08a; border: 1px solid rgba(29,185,84,0.3); }
.sh-refresh { background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.12); color: inherit; border-radius: 9px; width: 32px; height: 32px; display: inline-flex; align-items: center; justify-content: center; cursor: pointer; opacity: .8; transition: background .15s, transform .3s; }
.sh-refresh:hover { opacity: 1; background: rgba(255,255,255,0.12); }
.sh-refresh:active svg { transform: rotate(-180deg); transition: transform .4s; }
.sh-empty { display: flex; flex-direction: column; align-items: center; gap: 12px; text-align: center; font-size: 13.5px; opacity: .6; padding: 30px 18px; line-height: 1.55; }
.sh-empty-ico { display: inline-flex; opacity: .5; }
.sh-rooms { display: flex; flex-direction: column; gap: 14px; }
.sh-room { background: rgba(255,255,255,0.025); border: 1px solid rgba(255,255,255,0.07); border-radius: 14px; padding: 12px; }
.sh-room-head { display: flex; align-items: center; gap: 8px; padding: 2px 4px 10px; }
.sh-room-ico { display: inline-flex; opacity: .7; }
.sh-room-name { font-size: 13px; font-weight: 700; opacity: .92; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.sh-room-count { margin-left: auto; font-size: 11px; font-weight: 700; opacity: .6; padding: 1px 7px; border-radius: 999px; background: rgba(255,255,255,0.06); }
.sh-list { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 6px; }
.sh-li { position: relative; display: flex; align-items: stretch; gap: 6px; }
.sh-item { flex: 1; min-width: 0; text-align: left; display: flex; align-items: center; gap: 10px; padding: 11px 13px; border-radius: 10px;
  background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08); color: inherit; cursor: pointer; transition: background .15s, border-color .15s; }
.sh-item:hover { background: rgba(29,185,84,0.10); border-color: rgba(29,185,84,0.45); }
.sh-item-main { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 3px; }
.sh-item-date { font-size: 12.5px; font-weight: 700; }
.sh-item-preview { font-size: 12px; opacity: .65; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.sh-item-chev { display: inline-flex; opacity: .35; flex: 0 0 auto; }
.sh-item:hover .sh-item-chev { opacity: .7; }
.sh-del { flex: 0 0 auto; width: 38px; border-radius: 10px; border: 1px solid rgba(255,255,255,0.08); background: rgba(255,255,255,0.03);
  color: #9aa6b2; cursor: pointer; display: inline-flex; align-items: center; justify-content: center; opacity: 0; transition: opacity .15s, color .15s, background .15s, border-color .15s; }
.sh-li:hover .sh-del { opacity: .75; }
.sh-del:hover { opacity: 1; color: #ff7a7a; background: rgba(255,80,80,0.12); border-color: rgba(255,80,80,0.4); }
.sh-confirm { flex: 0 0 auto; display: inline-flex; align-items: center; gap: 5px; }
.sh-c-yes, .sh-c-no { border-radius: 9px; padding: 0 12px; font-size: 12px; font-weight: 700; cursor: pointer; border: 1px solid transparent; }
.sh-c-yes { background: #e5484d; color: #fff; }
.sh-c-yes:hover { filter: brightness(1.08); }
.sh-c-yes:disabled { opacity: .6; cursor: default; }
.sh-c-no { background: rgba(255,255,255,0.06); color: inherit; border-color: rgba(255,255,255,0.14); }
.sh-c-no:hover { background: rgba(255,255,255,0.12); }
`;

const SH_MODAL_CSS = `
.sh-backdrop { position: fixed; inset: 0; background: rgba(4,7,12,0.62); backdrop-filter: blur(3px); z-index: 2147483200; display: flex; align-items: center; justify-content: center; padding: 20px; }
.sh-modal { width: min(600px, 94vw); max-height: 86vh; display: flex; flex-direction: column; overflow: hidden; background: #11161e; color: #eaf0f6; border: 1px solid rgba(255,255,255,0.12); border-radius: 18px; box-shadow: 0 24px 70px rgba(0,0,0,0.65); animation: shIn .25s ease both; }
@keyframes shIn { from { opacity: 0; transform: translateY(14px) scale(.98); } to { opacity: 1; transform: none; } }
.sh-mhead { display: flex; align-items: flex-start; justify-content: space-between; padding: 18px 20px; border-bottom: 1px solid rgba(255,255,255,0.08); background: linear-gradient(180deg, rgba(29,185,84,0.07), transparent); }
.sh-mhead-l { display: flex; align-items: flex-start; gap: 11px; min-width: 0; }
.sh-mhead-ico { display: inline-flex; width: 34px; height: 34px; border-radius: 10px; align-items: center; justify-content: center; background: rgba(29,185,84,0.14); color: #57e08a; border: 1px solid rgba(29,185,84,0.28); flex: 0 0 auto; }
.sh-mtitle { font-size: 16px; font-weight: 700; word-break: break-word; }
.sh-mdate { font-size: 12.5px; opacity: .6; margin-top: 3px; }
.sh-close { background: none; border: none; color: #9aa6b2; font-size: 24px; line-height: 1; cursor: pointer; padding: 0 4px; }
.sh-close:hover { color: #fff; }
.sh-mbody { padding: 20px; overflow-y: auto; }
.sh-loading { opacity: .6; font-size: 13.5px; }
.sh-mfoot { display: flex; align-items: center; gap: 10px; padding: 14px 20px; border-top: 1px solid rgba(255,255,255,0.08); }
.sh-copy { padding: 9px 18px; border-radius: 10px; border: 1px solid rgba(255,255,255,0.2); background: transparent; color: #eaf0f6; cursor: pointer; font-size: 13.5px; }
.sh-copy:hover { background: rgba(255,255,255,0.09); }
.sh-del-text { margin-left: auto; padding: 9px 16px; border-radius: 10px; border: 1px solid rgba(255,80,80,0.4); background: rgba(255,80,80,0.10); color: #ff8a8a; cursor: pointer; font-size: 13.5px; font-weight: 600; }
.sh-del-text:hover { background: rgba(255,80,80,0.18); }
.sh-del-text:disabled { opacity: .6; cursor: default; }
`;
